import React, { useState } from "react";

import MarkerClusterGroup from '@changey/react-leaflet-markercluster';
import { Duration, ZonedDateTime } from "@js-joda/core";
import { DeviceStatus, MdmObjectDeviceRelationDto, MdmObjectDto, useGetAllDevicesByProjectQuery, useGetObjectQuery } from "gql/generated/types";
import L, { DivIcon, LatLngBounds, LatLngTuple } from "leaflet";
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { LayersControl, Marker, Popup, ZoomControl, useMap } from "react-leaflet";
import { Link } from "react-router-dom";
import { DEFAULT_DATE_FORMATTER } from "utils/constants";
import { convertTimeSpanToString, parseToLocalZoneDateTime, secondsToTimeSpan } from "utils/time-utils";
import useProjectContext from "../hooks/useProjectContext";
import { TableColumn } from "../utils/types";
import { renderDeviceStatus } from "../utils/utils";

const defaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconAnchor: [12, 40],
  popupAnchor: [0, -40],
});

const offlineIcon = L.icon({
  iconUrl: icon,
  className: "offline-icon",
  shadowUrl: iconShadow,
  iconAnchor: [12, 40],
  popupAnchor: [0, -40],
});

type ObjectMapContentProps = {
  objects: MdmObjectDto[];
};

const ObjectMapContent: React.FC<ObjectMapContentProps> = ({ objects }) => {
  const { projectId } = useProjectContext();
  const map = useMap();

  const { data: deviceData } = useGetAllDevicesByProjectQuery({
    variables: { id: projectId! },
    skip: !projectId,
  });

  const offlineDevices = deviceData?.mdmDevicesByProjectId?.filter(device => device.status.status === DeviceStatus.Offline);
  const offlineObjects = objects?.filter(object => offlineDevices?.some(device => device.objectRelation?.mdmObject?.id === object.id));

  const markers = objects?.reduce((tuples, object) => {
    if (!object?.location?.latitude || !object?.location?.longitude) return tuples;
    tuples.push([object?.location?.latitude, object?.location?.longitude]);
    return tuples;
  }, [] as LatLngTuple[]);

  if (markers.length === 0) map.fitBounds([[54.2, 23.5], [56.2, 24.5]]);
  if (markers.length === 1) map.setView(markers[0], 16);

  if (markers.length > 1) {
    const bounds = new LatLngBounds(markers);
    const northEast = Object.values(bounds.getNorthEast()) as LatLngTuple;
    const southWest = Object.values(bounds.getSouthWest()) as LatLngTuple;
    map.fitBounds([northEast, southWest]);
  }

  const markerGroups: LayerProps[] = [
    {
      layerName: "All Objects",
      markerIcon: defaultIcon,
      data: objects,
      checked: true
    },
    {
      layerName: "Offline Objects",
      markerIcon: offlineIcon,
      data: offlineObjects,
      checked: false
    }
  ]

  return (
    <div>
      <ZoomControl position="bottomright" />
      <LayersControl position="topright">
        {markerGroups.map(({ layerName, markerIcon, data, checked }) => (
          <Layer
            key={layerName}
            layerName={layerName}
            markerIcon={markerIcon}
            data={data}
            checked={checked}
          />
        ))}
      </LayersControl>
    </div>
  );
};

export default ObjectMapContent;

type LayerProps = {
  layerName: string;
  markerIcon: L.Icon<L.IconOptions> | DivIcon;
  data: MdmObjectDto[];
  checked: boolean;
};

const Layer: React.FC<LayerProps> = ({ layerName, markerIcon, data, checked }) => {
  const mapMarkers = data?.map(object => (
    <ObjectMarker key={`${object.id}-${layerName}`} object={object} markerIcon={markerIcon} />
  ));
  return (
    <LayersControl.BaseLayer checked={checked} name={layerName}>
      <MarkerClusterGroup maxClusterRadius={28}>{mapMarkers}</MarkerClusterGroup>
    </LayersControl.BaseLayer>
  );
}

type ObjectMarkerProps = {
  markerIcon: L.Icon<L.IconOptions> | DivIcon;
  object: MdmObjectDto;
};

const ObjectMarker: React.FC<ObjectMarkerProps> = ({ object, markerIcon }) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  if (!object.location?.latitude || !object.location?.longitude) return null;

  const position: LatLngTuple = [object.location.latitude, object.location.longitude];

  return (
    <Marker
      key={object.id}
      position={position}
      icon={markerIcon}
    >
      <Dialog
        header={`${object.title} - ${object.address?.fullAddress}`}
        visible={dialogOpen}
        onHide={() => setDialogOpen(false)}
        modal
        style={{ minWidth: "500px", minHeight: "400px" }}
      >
          <div>
            <p>{`latitude: ${object.location?.latitude}`}</p>
            <p>{`longitude: ${object.location?.longitude}`}</p>
          </div>
      </Dialog>
      <Popup minWidth={320}>
        <ObjectPopup object={object} />
      </Popup>
    </Marker>
  );
}

const ObjectPopup = ({ object }: { object: MdmObjectDto }) => {
  const { projectId } = useProjectContext();

  const {data, loading} = useGetObjectQuery({ variables: { id: object.id! }, fetchPolicy: "no-cache"});

  const handleOffset = (rowData: MdmObjectDeviceRelationDto) => {
    const measurementTime = rowData.mdmDevice?.status.lastMeasurementDate;
    if (!measurementTime) return "-";
    const offset = Duration.between(parseToLocalZoneDateTime(measurementTime), ZonedDateTime.now()) 
    return convertTimeSpanToString(secondsToTimeSpan(offset.seconds()));
  }


  const handleDeviceLink = (rowData: MdmObjectDeviceRelationDto) => (
    <Link to={`/mdm/${projectId}/devices/${rowData.mdmDevice?.id}`} className="hover:underline">
      {rowData.mdmDevice?.identifier}
    </Link>
  );
  
  const columns: TableColumn<MdmObjectDeviceRelationDto>[] = [
    {
      field: "mdmDevice.identifier",
      header: "Device",
      body: handleDeviceLink,
    },
    {
      field: "mdmDevice.status.status",
      header: "Status",
      body: rowData => renderDeviceStatus(rowData.mdmDevice),
    },
    {
      field: "mdmDevice.status.lastMeasurementDate",
      header: "Last Measurement",
      body: rowData =>
        rowData.mdmDevice?.status.lastMeasurementDate
          ? parseToLocalZoneDateTime(rowData.mdmDevice.status.lastMeasurementDate).format(
              DEFAULT_DATE_FORMATTER
            )
          : "N/A",
    },
    {
      header: "Δ",
      body: handleOffset,
      headerTooltip: "Time since last measurement",
      headerTooltipOptions: { position: "top" },
    }
  ];
  return (
    <div className="flex flex-column align-items-start">
      <Link
        to={`/mdm/${projectId}/objects/${object.id}`}
        className="cursor-pointer hover:underline w-25rem"
      >
        {object.title}
      </Link>
      <DataTable
        value={data?.mdmObjectById.deviceRelations}
        stripedRows
        loading={loading}
        size="small"
        className="mt-3 w-full"
      >
        {columns.map(col => (
          <Column key={col.header} {...col} />
        ))}
      </DataTable>
    </div>
  );
};
